'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Stack,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  Refresh as RefreshIcon,
  ReportProblem as ReportProblemIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error('App Error:', error);
  }, [error]);

  const handleGoToEmpty = () => {
    router.push('/empty');
  };

  const handleCopyError = () => {
    const errorText = `
    Error: ${error.message}
    Digest: ${error.digest || 'N/A'}
    Stack: ${error.stack || 'N/A'}
    Timestamp: ${new Date().toISOString()}
        `.trim();

    navigator.clipboard.writeText(errorText)
      .then(() => {
        console.log('Error details copied to clipboard');
      })
      .catch(console.error);
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #f9fafb 100%)',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <ReportProblemIcon
            sx={{
              fontSize: 80,
              color: 'error.main',
              opacity: 0.8,
            }}
          />
        </Box>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
          }}
        >
          Что-то пошло не так
        </Typography>
        <Alert
          severity="error"
          sx={{
            mb: 3,
            textAlign: 'left',
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="body1" sx={{ flexGrow: 1 }}>
              {error.message || 'Произошла непредвиденная ошибка'}
            </Typography>
            <Tooltip title="Скопировать детали ошибки">
              <IconButton
                size="small"
                onClick={handleCopyError}
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Alert>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 4,
            textAlign: 'left',
            maxHeight: 200,
            overflow: 'auto',
            backgroundColor: 'grey.50',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Детали ошибки:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
            }}
          >
            {error.stack || 'No stack trace available'}
            {error.digest && `\nDigest: ${error.digest}`}
          </Typography>
        </Paper>

        <Divider sx={{ my: 4 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ mt: 4 }}
        >
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={handleGoToEmpty}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              background: 'var(--sapphire-20)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Вернуться на главную
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={reset}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              },
            }}
          >
            Попробовать снова
          </Button>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 4, opacity: 0.7 }}
        >
          Если ошибка повторяется, пожалуйста, свяжитесь со службой поддержки
        </Typography>
      </Paper>
    </Container>
  );
}
